import type { HyperguestProductHotel } from '@travel-planner/shared/schemas';
import { MapPin, Sparkles } from 'lucide-react';
import { pickCss } from '@/shared/lib/css-utils';
import {
  Button,
  EmptyState,
  Pagination,
  PaginationButton,
  Spinner,
  Text,
  Tooltip,
} from '@/shared/ui';
import { scoreToLabel } from '../model/ai-score';
import { productRowDetails } from '../model/row-details';
import { formatLocation, resolveTitle } from '../model/title';
import css from './hyperguest-mapper.module.scss';
import { detailTooltipBubble, RowDetailTooltip } from './row-detail-tooltip';

const rowStyles = pickCss(css, css.mapperRow);
const badgeStyles = pickCss(css, css.aiBadge);

type Props = {
  rows: HyperguestProductHotel[];
  totalPages: number;
  currentPage: number;
  selectedId: string | null;
  scores: Map<string, number>;
  loading: boolean;
  onSelect: (id: string) => void;
  onPageChange: (page: number) => void;
};

export const ProductHotelsTable = ({
  rows,
  totalPages,
  currentPage,
  selectedId,
  scores,
  loading,
  onSelect,
  onPageChange,
}: Props) => (
  <div className={css.tablePane}>
    {loading ? (
      <Spinner />
    ) : rows.length === 0 ? (
      <EmptyState
        title="No unmatched products"
        description="Every product hotel already has a confirmed HyperGuest match."
      />
    ) : (
      <>
        <ul className={css.cardList}>
          {rows.map((row) => {
            const isSelected = row.id === selectedId;
            const score = scores.get(row.id);
            const badge = score !== undefined ? scoreToLabel(score) : null;
            return (
              <li className={rowStyles({ isSelected })} key={row.id}>
                <div className={css.rowMain}>
                  <Tooltip
                    position="bottom"
                    bubbleClassName={detailTooltipBubble}
                    content={
                      <RowDetailTooltip details={productRowDetails(row)} />
                    }
                  >
                    <Text weight="semibold" size="sm">
                      {resolveTitle(row.title)}
                    </Text>
                  </Tooltip>
                  <div className={css.metaLine}>
                    <MapPin className={css.metaIcon} />
                    <Text variant="muted" size="xs">
                      {formatLocation(row.city, row.country)}
                    </Text>
                  </div>
                  {badge && (
                    <span className={badgeStyles({ variant: badge.variant })}>
                      <Sparkles className={css.metaIcon} />
                      {badge.label} ({badge.percent}%)
                    </span>
                  )}
                </div>
                <div className={css.rowActions}>
                  <Button
                    size="sm"
                    variant={isSelected ? 'primary' : 'outline'}
                    onClick={() => onSelect(row.id)}
                  >
                    {isSelected ? 'Selected' : 'Select'}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
        {totalPages > 1 && (
          <Pagination>
            <PaginationButton
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
            >
              Prev
            </PaginationButton>
            <Text size="sm" variant="muted">
              Page {currentPage} of {totalPages}
            </Text>
            <PaginationButton
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange(currentPage + 1)}
            >
              Next
            </PaginationButton>
          </Pagination>
        )}
      </>
    )}
  </div>
);
